import { AppContextService } from '../../../services/app-context.service';
import { Component, DoCheck, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Observable, Subject, Subscription, concatMap, first, of, takeUntil, tap, catchError, EMPTY, map } from 'rxjs';
import { DataTableComponent, DataTableColumn } from '../../../shared/ui/data-table/data-table.component';
import { DataListComponent } from '../../../shared/ui/data-list/data-list.component';
import { DataFetch } from '../../../models/data-source';
import { DataSelectionBinding, DataViewSelection } from '../../../models/data-selection';
import { CampaignLeadPlanItem, CampaignMembersChange, LeadPlanPatch } from '../../../models/campaign.models';
import { CampaignApiService, campaignErrorMessage } from '../../../services/campaign-api.service';
import { LookupApiService, LeadActionTypeLookup, LeadAssigneeLookup } from '../../../services/lookup-api.service';
import { AuthService } from '../../../services/auth.service';
import { LeadsBoardComponent, WorkItem } from '../board/leads-board.component';
import { LeadsBoardStateService } from '../board/leads-board-state.service';
import { LEAD_LIST_COLUMNS } from '../board/lead-list-table';
import { ConfirmationDialogService } from '../../../shared/ui/confirmation-dialog/confirmation-dialog.service';
import { EditSessionRegistry } from '../../../shared/edit-session/edit-session-registry.service';
import { LeadFeature } from '../../../map/features/leads/lead-feature';
import { MapFeatureManagerService } from '../../../map/core/map-feature-manager.service';
import { MapSelectionContextService } from '../../../map/core/map-selection-context.service';
import { parseLocalDateString, toLocalDateString } from '../../../shared/date.util';
import { WorkspaceLayoutService } from '../../../services/workspace-layout.service';

/** Owned by the campaign page so paging and milestone previews preserve unsaved edits. */
export interface CampaignLeadPlanDraft {
  readonly original: Pick<WorkItem, keyof LeadPlanPatch>;
  readonly patch: LeadPlanPatch;
}

interface MembershipEdit {
  readonly change: CampaignMembersChange;
  readonly undo: boolean;
  /** Return to members and deselect the confirmed additions, preserving later map choices. */
  readonly finishAdding: boolean;
}

/**
 * Membership and next-action selection reuse the existing Campaign and Lead commands.
 * Other scheduling and engagement stay in Lead details.
 * Step 2 chooses Leads with the ordinary Leads selection: the Leads table's checkboxes and the
 * map edit the same selection, and "Add selected" links it to the campaign.
 */
@Component({
  selector: 'app-campaign-targets',
  templateUrl: './campaign-targets.component.html',
  styleUrls: ['./campaign-targets.component.css'],
  providers: [LeadsBoardStateService],
  host: { class: 'workspace-page workspace-page--full' },
})
export class CampaignTargetsComponent implements OnChanges, OnInit, DoCheck, OnDestroy {
  @Input({ required: true }) campaignId!: number;
  @Input() editable = false;
  @Input() step = 2;
  @Input() planDrafts = new Map<number, CampaignLeadPlanDraft>();
  @Output() changed = new EventEmitter<void>();
  @ViewChild('memberCollection') memberCollection?: LeadsBoardComponent;
  get table(): DataTableComponent<WorkItem> | undefined { return this.memberCollection?.listTable; }
  get list(): DataListComponent<WorkItem> | undefined { return this.memberCollection?.compactList; }
  private viewContext?: Subscription;
  /** Step 2 shows the Leads to choose from, or the Leads already linked. */
  choosing = false;
  error = '';
  notice = '';
  actionTypes: LeadActionTypeLookup[] = [];
  actionsLoading = false;
  actionsLoaded = false;
  actionsError = '';
  assignees: LeadAssigneeLookup[] = [];
  assigneesLoaded = false;
  assigneesLoading = false;
  assigneesError = '';
  bulkAssignee: number | null = null;
  bulkAction = '';
  bulkDue = '';
  /** Set from the select-all link once the whole page is ticked; covers every campaign Lead. */
  bulkAllMembers = false;
  /** The page checkbox selects the visible rows; only then is selecting the whole campaign offered. */
  get pageFullySelected(): boolean { return !!this.table?.isAllSelected() || !!this.list?.allLoadedSelected; }
  get allMembersSelected(): boolean { return this.bulkAllMembers && this.pageFullySelected; }
  selectAllMembers(): void { if (this.pageFullySelected) this.bulkAllMembers = true; }
  clearBulkSelection(selection: DataViewSelection<WorkItem>): void { this.bulkAllMembers = false; selection.clear(); }
  members: ReadonlySet<number> = new Set();
  membersLoaded = false;
  /** How many Leads the map selection holds, including unresolved cluster members. */
  selectedCount = 0;
  private resolvedSelectionIds: readonly number[] = [];
  private get resolvedAddIds(): readonly number[] { return this.selectionLoading || this.selectionError ? [] : this.resolvedSelectionIds.filter(id => !this.members.has(id)); }
  selectionFilter: 'all' | 'selected' | 'unselected' = 'all';
  get filteredSelectionIds(): readonly number[] | null {
    return this.selectionFilter === 'all' ? null : this.resolvedSelectionIds;
  }
  setSelectionFilter(value: string): void {
    if (value === 'all' || value === 'selected' || value === 'unselected') this.selectionFilter = value;
  }
  get pendingAddCount(): number { return this.resolvedAddIds.length; }
  selectionLoading = false;
  selectionError = '';
  private selectionRequest?: Subscription;
  /** The inverse of the last saved change; one level of undo. */
  undoChange: CampaignMembersChange | null = null;
  /** The Leads table's checkboxes are the Leads map selection, as in the Selection Workspace. */
  readonly leadSelection: DataSelectionBinding;
  private bindLeadSelection(): DataSelectionBinding {
    const selectedCount = () => this.selectedCount;
    return {
    get selectedCount() { return selectedCount(); },
    key: 'map-object-selection:leads',
    changed$: this.leadLayer.selectionCount$,
    has: id => typeof id === 'number' && this.leadLayer.isObjectSelected(id),
    setSelected: (ids, selected) => this.leadLayer.setObjectsSelected(
      ids.filter((id): id is number => typeof id === 'number'), selected),
    };
  }
  private sessionValid = true;
  /** Held while this page shows the Leads selection, so selecting on the map stays here. */
  private releasePresentation?: () => void;
  private pending = 0;
  private memberRequest?: Subscription;
  private actionRequest?: Subscription;
  private assigneeRequest?: Subscription;
  private readonly edits = new Subject<Observable<MembershipEdit | null>>();
  private readonly stopped$ = new Subject<void>();
  private readonly unregister: () => void;
  readonly targetColumns = LEAD_LIST_COLUMNS.filter(column => ['title', 'address', 'boardColumn', 'salesRep', 'nextAction'].includes(column.id));
  readonly actionColumns: readonly DataTableColumn[] = [
    ...LEAD_LIST_COLUMNS.filter(column => ['title', 'salesRep', 'nextAction'].includes(column.id))
      .map(column => column.id === 'salesRep' ? { ...column, label: 'Assignee' } : column),
    { id: 'nextActionDue', label: 'Due date' },
  ];
  readonly followUpColumns = [...this.actionColumns, LEAD_LIST_COLUMNS.find(column => column.id === 'boardColumn')!];
  get columns(): readonly DataTableColumn[] {
    return this.step === 3 ? this.actionColumns : this.step === 4 ? this.followUpColumns : this.targetColumns;
  }
  readonly fetch: DataFetch<WorkItem> = query => this.api.targets(this.campaignId, query).pipe(map(page => ({
    ...page, items: page.items.map(row => ({ ...row, ...this.planDrafts.get(row.leadId)?.patch })),
  })));
  get busy(): boolean { return this.pending > 0; }
  get dirty(): boolean { return this.busy || this.planDrafts.size > 0; }
  get canManage(): boolean { return this.sessionValid && this.editable && this.auth.hasPermission('Lead.Manage'); }
  get actionStep(): boolean { return this.step === 3 || this.step === 4; }
  get selectable(): boolean { return this.step === 2 && this.canManage; }
  /** Step 2 choosing view: the Leads collection bound to the map selection. */
  get choosingLeads(): boolean { return this.selectable && this.choosing; }
  get mapSelectionVisible(): boolean { return this.layout.dockedRight && this.leadLayer.enabled; }

  constructor(private readonly api: CampaignApiService,
    public readonly auth: AuthService, private readonly confirmation: ConfirmationDialogService,
    registry: EditSessionRegistry, private readonly leadLayer: LeadFeature,
    private readonly features: MapFeatureManagerService, private readonly layout: WorkspaceLayoutService,
    private readonly selectionContext: MapSelectionContextService,
    private readonly lookups: LookupApiService,
    private readonly context?: AppContextService) {
    this.unregister = registry.register(this);
    this.leadSelection = this.bindLeadSelection();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['step'] && !changes['step'].firstChange) {
      this.choosing = false;
      this.refreshSelectionCount();
      if (!this.actionStep) {
        this.table?.selection.resetLocal(); this.list?.selection.resetLocal();
      }
    }
    if (this.actionStep && !this.actionsLoaded && !this.actionsLoading) this.loadActionTypes();
    if (this.actionStep && !this.assigneesLoaded && !this.assigneesLoading) this.loadAssignees();
  }
  ngOnInit(): void {
    this.viewContext = this.context?.registerView({
      key: `campaign-targets:${this.campaignId}`, name: 'Campaign targets', priority: -15,
      read: () => ({ choosing: this.choosing, selectionFilter: this.selectionFilter }), describe: state => state.choosing ? 'Adding Leads' : 'Campaign members',
      restore: state => { this.setSelectionFilter(state.selectionFilter ?? 'all'); this.showChoosing(state.choosing === true); },
    });
    this.auth.tenant.sessionChanged$.pipe(takeUntil(this.stopped$)).subscribe(() => {
      this.sessionValid = false; this.stopped$.next(); this.pending = 0; this.members = new Set();
      this.membersLoaded = false; this.undoChange = null;
      this.actionTypes = []; this.actionsLoaded = false; this.actionsLoading = false;
      this.assignees = []; this.assigneesLoaded = false; this.assigneesLoading = false;
      this.bulkAssignee = null; this.bulkAction = this.bulkDue = ''; this.planDrafts.clear();
      this.resolvedSelectionIds = []; this.selectionLoading = false;
    });
    // One write at a time, in request order; each plan reads the members left by the previous one.
    this.edits.pipe(concatMap(edit => edit.pipe(concatMap(planned => planned ? this.write(planned) : EMPTY))),
      takeUntil(this.stopped$)).subscribe();
    this.leadLayer.selectionCount$.pipe(takeUntil(this.stopped$)).subscribe(count => { this.selectedCount = count; this.refreshSelectionCount(); });
    this.loadMembers();
    if (this.actionStep && !this.actionsLoaded && !this.actionsLoading) this.loadActionTypes();
    if (this.actionStep && !this.assigneesLoaded && !this.assigneesLoading) this.loadAssignees();
  }
  ngDoCheck(): void {
    if (this.choosingLeads === !!this.releasePresentation) return;
    if (this.choosingLeads) this.releasePresentation = this.selectionContext.presentHere();
    else { this.releasePresentation?.(); this.releasePresentation = undefined; }
  }
  loadMembers(): void {
    this.membersLoaded = false;
    this.memberRequest?.unsubscribe();
    this.memberRequest = this.api.memberLocations(this.campaignId).pipe(takeUntil(this.stopped$)).subscribe({
      next: rows => { this.members = new Set(rows.map(row => row.leadId)); this.membersLoaded = true; this.refreshSelectionCount(); },
      error: error => { this.error = campaignErrorMessage(error, 'Could not load campaign targets. Retry before changing targets.'); },
    });
  }
  loadActionTypes(): void {
    if (!this.sessionValid) return;
    this.actionRequest?.unsubscribe();
    this.actionsLoading = true; this.actionsError = '';
    this.actionRequest = this.lookups.leadActionTypes().pipe(takeUntil(this.stopped$)).subscribe({
      next: actions => {
        this.actionTypes = actions; this.actionsLoaded = true; this.actionsLoading = false;
      },
      error: () => {
        this.actionsLoading = false;
        this.actionsError = 'Could not load Lead actions.';
      },
    });
  }
  loadAssignees(): void {
    if (!this.sessionValid) return;
    this.assigneeRequest?.unsubscribe();
    this.assigneesLoading = true; this.assigneesError = '';
    this.assigneeRequest = this.lookups.leadAssignees().pipe(takeUntil(this.stopped$)).subscribe({
      next: assignees => {
        this.assignees = assignees; this.assigneesLoaded = true; this.assigneesLoading = false;
      },
      error: () => {
        this.assigneesLoading = false; this.assigneesError = 'Could not load assignees.';
      },
    });
  }
  hasAction(value: string | null): boolean { return this.actionTypes.some(action => action.label === value); }
  hasAssignee(id: number | null): boolean { return this.assignees.some(assignee => assignee.id === id); }
  assigneeLabel(id: number | null): string {
    if (id == null) return 'Not assigned';
    const assignee = this.assignees.find(item => item.id === id);
    return assignee?.displayName || assignee?.userName || `User #${id}`;
  }
  dueDateValue(value: string | null): string {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : toLocalDateString(date);
  }
  private dueTimestamp(value: string): string | null {
    const date = parseLocalDateString(value);
    return date && date.getFullYear() >= 2000 && toLocalDateString(date) === value ? date.toISOString() : null;
  }
  setAction(row: WorkItem, value: string): void { this.stagePlans([row], { nextActionType: value }); }
  setAssignee(row: WorkItem, value: string): void { this.stagePlans([row], { salesRepUserId: Number(value) }); }
  setDueDate(row: WorkItem, value: string): void {
    const due = this.dueTimestamp(value);
    if (!due) { this.error = 'Choose a valid due date.'; return; }
    if (value === this.dueDateValue(row.nextActionDue)) return;
    this.stagePlans([row], { nextActionDue: due });
  }
  get hasBulkChanges(): boolean { return this.bulkAssignee != null || !!this.bulkAction || !!this.bulkDue; }
  applyBulk(selection: DataViewSelection<WorkItem>): void {
    const patch: LeadPlanPatch = {};
    if (this.bulkAssignee != null) patch.salesRepUserId = this.bulkAssignee;
    if (this.bulkAction) patch.nextActionType = this.bulkAction;
    if (this.bulkDue) {
      const due = this.dueTimestamp(this.bulkDue);
      if (!due) { this.error = 'Choose a valid due date.'; return; }
      patch.nextActionDue = due;
    }
    if (!this.actionStep || !this.canManage || this.busy || !this.membersLoaded
      || (patch.nextActionType !== undefined && !this.hasAction(patch.nextActionType))
      || (patch.salesRepUserId !== undefined && !this.hasAssignee(patch.salesRepUserId))) return;
    const ids = this.allMembersSelected ? [...this.members]
      : selection.selected.map(row => row.leadId).filter(id => this.members.has(id));
    if (!ids.length) return;
    // Saved at once: the bulk values replace those fields in any unsaved row edits.
    this.writePlans(ids.map(leadId => ({ leadId, ...patch })), updated => {
      for (const id of updated) {
        const draft = this.planDrafts.get(id);
        if (!draft) continue;
        for (const key of Object.keys(patch) as (keyof LeadPlanPatch)[]) delete draft.patch[key];
        if (!Object.keys(draft.patch).length) this.planDrafts.delete(id);
      }
      this.bulkAssignee = null; this.bulkAction = this.bulkDue = ''; this.bulkAllMembers = false;
      selection.clear();
    });
  }
  private stagePlans(rows: WorkItem[], patch: LeadPlanPatch): void {
    if (!this.actionStep || !this.canManage || this.busy || !this.membersLoaded || !Object.keys(patch).length
      || (patch.nextActionType !== undefined && !this.hasAction(patch.nextActionType))
      || (patch.salesRepUserId !== undefined && !this.hasAssignee(patch.salesRepUserId))) return;
    this.error = this.notice = '';
    for (const row of rows) {
      if (!this.members.has(row.leadId)) continue;
      const draft = this.planDrafts.get(row.leadId) ?? {
        original: { salesRepUserId: row.salesRepUserId, nextActionType: row.nextActionType, nextActionDue: row.nextActionDue },
        patch: {},
      };
      for (const [field, value] of Object.entries(patch)) {
        const key = field as keyof LeadPlanPatch;
        const unchanged = key === 'nextActionDue'
          ? this.dueDateValue(String(value)) === this.dueDateValue(draft.original.nextActionDue)
          : value === draft.original[key];
        if (unchanged) delete draft.patch[key];
        else Object.assign(draft.patch, { [key]: value });
      }
      Object.assign(row, draft.original, draft.patch);
      if (Object.keys(draft.patch).length) this.planDrafts.set(row.leadId, draft);
      else this.planDrafts.delete(row.leadId);
    }
  }
  /** Explicit Save: every edited Lead in one campaign request. */
  savePlans(): void {
    if (!this.actionStep || !this.canManage || this.busy || !this.membersLoaded || !this.planDrafts.size) return;
    const items = [...this.planDrafts].filter(([id]) => this.members.has(id))
      .map(([leadId, draft]) => ({ leadId, ...draft.patch }));
    this.writePlans(items, updated => { for (const id of updated) this.planDrafts.delete(id); });
  }
  private writePlans(items: CampaignLeadPlanItem[], saved: (updated: number[]) => void): void {
    if (!items.length) return;
    this.pending++; this.error = this.notice = '';
    this.api.updateLeadPlans(this.campaignId, items).pipe(takeUntil(this.stopped$)).subscribe({
      next: result => {
        this.pending--;
        saved(result.updated);
        this.notice = `${result.updated.length} Lead${result.updated.length === 1 ? '' : 's'} saved.`;
        if (result.skipped.length) this.error = `${result.skipped.length} Lead${result.skipped.length === 1 ? ' was' : 's were'} not saved because they are no longer in this campaign.`;
        this.memberCollection?.refresh(); this.changed.emit();
      },
      error: error => {
        this.pending--;
        this.error = campaignErrorMessage(error, 'Could not save these Leads. Your changes are still here. Try again.');
      },
    });
  }
  /** Resolves a count from the existing selection owner, including unloaded cluster members. */
  refreshSelectionCount(): void {
    this.selectionRequest?.unsubscribe();
    this.selectionLoading = false; this.selectionError = '';
    if (!this.choosingLeads || !this.membersLoaded || !this.selectedCount) { this.resolvedSelectionIds = []; return; }
    this.selectionLoading = true;
    this.selectionRequest = this.leadLayer.resolveSelectedObjects().pipe(first(), takeUntil(this.stopped$)).subscribe({
      next: objects => {
        this.resolvedSelectionIds = [...new Set([...this.leadLayer.selectedObjectIds, ...objects.map(object => object.id)])];
        this.selectionLoading = false;
      },
      error: () => { this.selectionLoading = false; this.selectionError = 'Could not count the selected Leads. Retry before adding.'; },
    });
  }
  /** Adding uses the ordinary shared selection; this only exposes the map. */
  selectOnMap(): void {
    if (!this.selectable || this.busy) return;
    this.showChoosing(true);
    this.layout.openAs('docked-right');
    if (!this.leadLayer.enabled) this.features.toggleFeature(this.leadLayer.key);
  }
  showChoosing(choosing: boolean): void {
    if (this.busy || (choosing && !this.selectable)) return;
    this.choosing = choosing;
    this.error = this.notice = '';
    this.refreshSelectionCount();
  }
  /** Confirms the selection: every selected Lead, including unloaded cluster members, joins the campaign. */
  addSelected(): void {
    if (!this.choosingLeads || this.busy || !this.membersLoaded || this.selectionLoading || !this.pendingAddCount) return;
    this.queue(of(this.plan([...this.resolvedAddIds], [], false, true)));
  }
  /** Removing a member is explicit and separate from picking Leads to add. */
  removeMember(row: WorkItem): void {
    if (!this.selectable || !this.membersLoaded || !this.members.has(row.leadId)) return;
    this.confirmation.confirm({ title: 'Remove campaign target?',
      message: `${row.title || 'This Lead'} will leave this campaign. Its record and follow-ups will be preserved.`,
      confirmLabel: 'Remove', cancelLabel: 'Keep', danger: true })
      .pipe(takeUntil(this.stopped$)).subscribe(confirmed => {
        if (confirmed && this.selectable) this.queue(of(this.plan([], [row.leadId], false)));
      });
  }
  undo(): void {
    const change = this.undoChange;
    if (!this.canManage || !this.membersLoaded || !change) return;
    this.queue(of(this.plan(change.add, change.remove, true)));
  }
  ngOnDestroy(): void {
    this.viewContext?.unsubscribe();
    this.releasePresentation?.(); this.releasePresentation = undefined;
    this.sessionValid = false; this.unregister(); this.stopped$.next(); this.stopped$.complete();
  }

  private plan(add: readonly number[], remove: readonly number[], undo: boolean, finishAdding = false): MembershipEdit | null {
    if (!add.length && !remove.length) {
      if (finishAdding) this.notice = 'The selected Leads are already in this campaign.';
      return null;
    }
    return { change: { add: [...add], remove: [...remove] }, undo, finishAdding };
  }
  private queue(edit: Observable<MembershipEdit | null>): void {
    this.pending++; this.error = this.notice = '';
    this.edits.next(edit.pipe(tap({ next: planned => { if (!planned) this.pending--; } })));
  }
  private write(edit: MembershipEdit): Observable<unknown> {
    return this.api.changeMembers(this.campaignId, edit.change).pipe(takeUntil(this.stopped$), tap({
      next: result => {
        const members = new Set(this.members);
        for (const id of result.added) members.add(id);
        for (const id of result.removed) { members.delete(id); this.planDrafts.delete(id); }
        this.members = members; this.pending--;
        const count = result.added.length + result.removed.length;
        this.undoChange = count && !edit.undo ? { add: result.removed, remove: result.added } : null;
        this.notice = edit.undo ? 'Last change undone.' : count ? [
          result.added.length && `${result.added.length} Lead${result.added.length === 1 ? '' : 's'} added`,
          result.removed.length && `${result.removed.length} removed`,
        ].filter(Boolean).join(', ') + '.' : 'No targets changed.';
        if (edit.finishAdding) { this.choosing = false; this.leadLayer.setObjectsSelected(edit.change.add, false); }
        this.refreshSelectionCount();
        this.memberCollection?.refresh(); this.changed.emit();
      },
      error: error => {
        this.pending--;
        this.error = campaignErrorMessage(error, 'Could not change campaign targets. Your targets are unchanged.');
      },
    }), catchError(() => EMPTY));
  }
}
